import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

// A one-off "flash" highlight on a block, applied as a node decoration so it
// survives ProseMirror re-rendering the node (e.g. when spellcheck decorations
// land). Reuses the app's `section-highlight-fade` fade-out animation. Set the
// target via a `{ from, to }` meta; clear it with a `{ clear: true }` meta.
type FlashMeta = { from: number; to: number } | { clear: true };

export const flashPluginKey = new PluginKey<DecorationSet>('laudopsiFlash');

const FLASH_CLASS = 'section-highlight-fade';
const FLASH_COLOR = '#dbeafe';

export const createFlashPlugin = () =>
    new Plugin<DecorationSet>({
        key: flashPluginKey,
        state: {
            init: () => DecorationSet.empty,
            apply(transaction, previous, _oldState, newState) {
                const meta = transaction.getMeta(flashPluginKey) as FlashMeta | undefined;

                if (meta) {
                    if ('clear' in meta) {
                        return DecorationSet.empty;
                    }

                    const decoration = Decoration.node(meta.from, meta.to, {
                        class: FLASH_CLASS,
                        style: `--section-highlight-color: ${FLASH_COLOR}`,
                    });

                    return DecorationSet.create(newState.doc, [decoration]);
                }

                if (transaction.docChanged) {
                    return previous.map(transaction.mapping, transaction.doc);
                }

                return previous;
            },
        },
        props: {
            decorations(state) {
                return flashPluginKey.getState(state) ?? DecorationSet.empty;
            },
        },
    });
